import { useCtaUrl } from '@hyphengroup/cms-components/components/ApplyCta';
import { getApplyButtonLabel } from '@hyphengroup/cms-components/utils/helpers/router';
import { useMemo } from 'react';
import { tr } from '../../helpers';
import {
  getProductRewardBadgeCopy,
  getProductSharedAttributeData,
  RewardBadgeStaticText,
} from '../../helpers/productRecommendation';
import { EditorsPickViewModel } from './types';
import { ComponentEditorPickProduct } from '$/(cms)/_modules/cmsService/types';

export const useEditorsPickViewModel = (props: {
  item?: ComponentEditorPickProduct;
  lang: string;
  rewardBadgeStaticText: RewardBadgeStaticText;
}) => {
  const { item, lang, rewardBadgeStaticText } = props;
  const product = item?.product;
  // Editor's Pick renders on CMS pages inside CmsClientProvider, so the CTA URL
  // resolves through the shared hook — page vertical/pageType from context, plus
  // UTM routing. CMS-1747.
  const applyUrl = useCtaUrl({ product }) ?? '';

  const attribute = useMemo(() => {
    if (!product) {
      return { value: '', tooltip: '' };
    }

    return getProductSharedAttributeData(
      product,
      item?.display_attribute?.name,
    );
  }, [item?.display_attribute?.name, product]);

  const badge = useMemo(() => {
    if (!product) {
      return '';
    }

    return getProductRewardBadgeCopy({
      product,
      staticText: rewardBadgeStaticText,
    });
  }, [product, rewardBadgeStaticText]);

  const applyLabel = useMemo(() => {
    if (!product) {
      return '';
    }

    return getApplyButtonLabel(
      tr('apply_now', lang),
      undefined,
      product.call_to_action,
    );
  }, [lang, product]);

  return {
    item,
    product,
    attributeValue: attribute.value,
    attributeTooltip: attribute.tooltip,
    badge,
    applyUrl,
    applyLabel,
    primaryMediaSrc: product?.product_image || '',
    fallbackMediaSrc: product?.provider?.provider_image || '',
    fallbackText:
      product?.provider?.provider_name || product?.product_name || '',
    showDetails: Boolean(product?.url_alias),
    showApply: Boolean(applyUrl),
  } satisfies EditorsPickViewModel;
};
